import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TicketsService, ConfiguracoesService, DepartamentosService } from "@/services";
import { Ticket as TicketType, TicketResposta } from "@/types";
import toast from "react-hot-toast";
import {
  TicketResponses,
  TicketModals,
  AccessDenied,
  LoadingSpinner,
  TagsManager,
  RelatedTickets,
  InternalComments,
  FilePreviewModals,
  SystemMessagesModal,
  TicketInfoSidebar,
} from "@/components/Tickets";
import { useTicketActions } from "@/hooks/useTicketActions";
import { useTicketResponses } from "@/hooks/useTicketResponses";
import { useFileHandlers } from "@/hooks/useFileHandlers";
import { checkTicketAccess } from "@/utils/ticketPermissions";
import "@/styles/ticketDetail.css";

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const { usuario } = useAuth();
  const [ticket, setTicket] = useState<TicketType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newResponse, setNewResponse] = useState("");
  const [anexos, setAnexos] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showFecharModal, setShowFecharModal] = useState(false);
  const [showExcluirModal, setShowExcluirModal] = useState(false);
  const [showTransferirModal, setShowTransferirModal] = useState(false);
  const [departamentos, setDepartamentos] = useState<
    Array<{ id: number; nome: string; ativo: number }>
  >([]);
  const [departamentoSelecionado, setDepartamentoSelecionado] =
    useState<number>(0);
  const [orderAsc, setOrderAsc] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const [showSystemMessages, setShowSystemMessages] = useState(false);
  const [pdfRenderWidth, setPdfRenderWidth] = useState<number>(900);

  // Hooks customizados
  const ticketActions = useTicketActions({
    ticket,
    usuario,
    ticketId: id ? parseInt(id) : undefined,
    onTicketUpdate: (updated) => {
      if (ticket) {
        setTicket({ ...ticket, ...updated });
      }
    },
    onTicketDeleted: () => {
      // Navegação será feita pelo hook
    },
    onCloseFecharModal: () => {
      setShowFecharModal(false);
    },
  });

  const ticketResponses = useTicketResponses({
    ticket,
    usuario,
    ticketId: id ? parseInt(id) : undefined,
    onResponseAdded: async (response) => {
      await loadTicket();
      setNewResponse("");
      setAnexos([]);
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        quill.setText("");
      }
    },
    onResponseUpdated: (responseId, content) => {
      if (ticket) {
        setTicket({
          ...ticket,
          respostas:
            ticket.respostas?.map((resp) =>
              resp.id === responseId
                ? {
                    ...resp,
                    conteudo: content,
                    updated_at: new Date().toISOString(),
                  }
                : resp
            ) || [],
        });
      }
    },
    onResponseDeleted: (responseId) => {
      if (ticket) {
        setTicket({
          ...ticket,
          respostas: ticket.respostas?.filter((resp) => resp.id !== responseId) || [],
        });
      }
    },
  });

  const fileHandlers = useFileHandlers();
  const quillRef = useRef<any>(null);

  useEffect(() => {
    const update = () => {
      try {
        const w = typeof window !== "undefined" ? window.innerWidth : 900;
        setPdfRenderWidth(Math.min(900, Math.floor(w * 0.9)));
      } catch {
        setPdfRenderWidth(900);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const [configuracoes, setConfiguracoes] = useState({
    arquivos_permitidos: "jpg, png, pdf, doc, docx, xls, xlsx",
    maximo_arquivos: "5",
    tamanho_maximo: "10",
  });

  useEffect(() => {
    if (id) {
      if (!usuario) {
        return;
      }
      loadTicket();
    }
  }, [id, usuario]);

  useEffect(() => {
    if (ticket) {
      loadDepartamentos();
    }
  }, [ticket]);

  // Carregar configurações do sistema
  useEffect(() => {
    loadConfiguracoes();
  }, []);

  const loadTicket = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const response = await TicketsService.getTicket(parseInt(id));

      // O backend já faz a verificação de acesso (incluindo menções)
      // Se chegou aqui, o backend permitiu o acesso
      // Apenas verificar se o ticket existe
      if (response) {
        setTicket(response);
      } else {
        setTicket(null);
      }
    } catch (error: any) {
      setTicket(null);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConfiguracoes = async () => {
    try {
      const response = await ConfiguracoesService.getConfiguracoes();
      if (Array.isArray(response)) {
        const config = response.reduce((acc: any, item: any) => {
          acc[item.nome] = item.valor;
          return acc;
        }, {});

        setConfiguracoes({
          arquivos_permitidos:
            config.arquivos_permitidos || "jpg, png, pdf, doc, docx, xls, xlsx",
          maximo_arquivos: config.maximo_arquivos || "5",
          tamanho_maximo: config.tamanho_maximo || "10",
        });
      }
    } catch (error) {
      // Configurações não carregadas, usar valores padrão
    }
  };

  const loadDepartamentos = async () => {
    if (!ticket) return;

    try {
      const response = await DepartamentosService.getDepartamentos();
      if (response) {
        const departamentosAtivos = response.filter(
          (dept) => dept.ativo === 1 && dept.id !== ticket?.departamento_id
        );
        setDepartamentos(departamentosAtivos);

        if (departamentosAtivos.length > 0) {
          setDepartamentoSelecionado(departamentosAtivos[0].id);
        }
      }
    } catch (error) {
      // Departamentos não carregados
    }
  };

  // Função para iniciar atendimento
  const handleIniciarAtendimento = async () => {
    if (!ticket || !id) return;

    try {
      const response = await TicketsService.iniciarAtendimento(parseInt(id));
      
      if (response.ticket) {
        // Recarregar o ticket completo para garantir que todas as respostas sejam carregadas
        await loadTicket();
        toast.success(response.mensagem || 'Atendimento iniciado com sucesso!');
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.error || 
        error.message || 
        'Erro ao iniciar atendimento'
      );
    }
  };

  // Verificar se o operador pode enviar resposta (ticket deve estar em andamento)
  const podeEnviarResposta = useMemo(() => {
    if (!ticket || !usuario) return false;
    
    // Se não for operador, pode responder normalmente
    if (usuario.nivel !== 'admin' && usuario.nivel !== 'dev') {
      return ticket.pode_responder || false;
    }
    
    // Se o operador foi quem criou o ticket, pode responder normalmente
    // (mesmo que o ticket esteja aberto e não tenha iniciado atendimento)
    if (ticket.usuario_id === usuario.id) {
      return ticket.pode_responder || false;
    }
    
    // Se for operador (mas não o criador), só pode responder se o ticket estiver em andamento (status_id = 3)
    // ou se o ticket já estiver fechado (pode reabrir)
    if (ticket.status_id === 1) {
      // Ticket aberto - operador precisa iniciar atendimento primeiro
      return false;
    }
    
    return ticket.pode_responder || false;
  }, [ticket, usuario]);

  // Handlers de arquivo (mantidos localmente pois são específicos do formulário)

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const newFiles = Array.from(files);

    const maxFiles = parseInt(configuracoes.maximo_arquivos) || 5;
    if (anexos.length + newFiles.length > maxFiles) {
      toast.error(
        `Máximo de ${maxFiles} arquivos permitidos. Você já tem ${anexos.length} arquivo(s).`
      );
      return;
    }

    const validFiles = newFiles.filter((file) => {
      const maxSize = parseInt(configuracoes.tamanho_maximo) * 1024 * 1024;
      const allowedExtensions = configuracoes.arquivos_permitidos
        .split(",")
        .map((ext) => ext.trim().toLowerCase());

      const mimeTypeMap: { [key: string]: string[] } = {
        jpg: ["image/jpeg"],
        jpeg: ["image/jpeg"],
        png: ["image/png"],
        gif: ["image/gif"],
        pdf: ["application/pdf"],
        txt: ["text/plain"],
        doc: ["application/msword"],
        docx: [
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ],
        xls: ["application/vnd.ms-excel"],
        xlsx: [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ],
      };

      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        toast.error(
          `Tipo de arquivo ${file.name} não é permitido. Tipos aceitos: ${configuracoes.arquivos_permitidos}`
        );
        return false;
      }

      const allowedMimeTypes = mimeTypeMap[fileExtension] || [];
      if (
        allowedMimeTypes.length > 0 &&
        !allowedMimeTypes.includes(file.type)
      ) {
        toast.error(`Tipo de arquivo ${file.name} não é válido`);
        return false;
      }

      if (file.size > maxSize) {
        toast.error(
          `Arquivo ${file.name} é muito grande. Máximo: ${configuracoes.tamanho_maximo}MB`
        );
        return false;
      }

      return true;
    });

    setAnexos((prev) => [...prev, ...validFiles]);
  };

  const removeAnexo = (index: number) => {
    setAnexos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Funções de arquivo movidas para useFileHandlers hook

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Funções de edição/exclusão de mensagens movidas para useTicketResponses hook

  const handleAITextImprovement = (improvedText: string) => {
    setNewResponse(improvedText);
  };

  const renderUserInfo = (resposta: TicketResposta) => {
    const userLevel = resposta.usuario_nivel;

    if (userLevel === "dev" || userLevel === "admin") {
      return (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {resposta.usuario_departamentos &&
          resposta.usuario_departamentos.length > 0 ? (
            resposta.usuario_departamentos.map((dept) => (
              <span
                key={dept.id}
                className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700"
              >
                {dept.nome}
              </span>
            ))
          ) : (
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              Nenhum departamento
            </span>
          )}
        </div>
      );
    } else {
      return (
        <div className="flex flex-wrap gap-1.5 mt-1">
          {resposta.usuario_unidades && resposta.usuario_unidades.length > 0 ? (
            resposta.usuario_unidades.map((unidade) => (
              <span
                key={unidade.id}
                className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700"
              >
                {unidade.nome}
              </span>
            ))
          ) : (
            <span className="text-[10px] text-gray-500 dark:text-gray-400">Nenhuma unidade</span>
          )}
        </div>
      );
    }
  };

  // Respostas ordenadas conforme a escolha do usuário
  const sortedRespostas = useMemo(() => {
    if (!ticket?.respostas) return [];
    const arr = [...ticket.respostas];
    arr.sort((a: any, b: any) => {
      const ta = new Date(a.created_at || a.createdAt || 0).getTime();
      const tb = new Date(b.created_at || b.createdAt || 0).getTime();
      return orderAsc ? ta - tb : tb - ta;
    });
    return arr;
  }, [ticket?.respostas, orderAsc]);

  // Usar timeline do backend ao invés de filtrar mensagens do sistema
  // As respostas já vêm filtradas (sem usuario_id = 1) do backend
  const userSortedRespostas = sortedRespostas;
  const systemRespostas = ticket?.timeline || [];

  // Versão paginada/visível do ticket: passamos apenas as primeiras `visibleCount` respostas
  const displayedRespostas = useMemo(() => {
    if (!userSortedRespostas) return [];
    return userSortedRespostas.slice(0, visibleCount);
  }, [userSortedRespostas, visibleCount]);

  const displayedTicket = ticket
    ? { ...ticket, respostas: displayedRespostas }
    : ticket;

  // Função exportToPDF movida para utils/pdfExporter.ts
  const handleExportPDF = () => {
    if (!ticket) return;
    import("@/utils/pdfExporter").then(({ exportTicketToPDF }) => {
      // Converter timeline para formato compatível com PDF (se necessário)
      exportTicketToPDF(ticket, systemRespostas);
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!ticket || !usuario || !checkTicketAccess(ticket, usuario)) {
    return <AccessDenied usuario={usuario} ticket={ticket} />;
  }

  return (
    <div className="space-y-4 sm:space-y-5 md:space-y-6 px-3 sm:px-4 md:px-0 pb-4 sm:pb-0">
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {/* Left Column - Responses and Form */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-5 md:space-y-6">
          <TicketResponses
            ticket={displayedTicket}
            usuario={usuario}
            editingMessageId={ticketResponses.editingMessageId}
            editingContent={ticketResponses.editingContent}
            onEdit={ticketResponses.handleEditMessage}
            onDelete={ticketResponses.handleDeleteMessage}
            onCancelEdit={ticketResponses.handleCancelEdit}
            onSaveEdit={ticketResponses.handleSaveEdit}
            onContentChange={ticketResponses.setEditingContent}
            onDownload={fileHandlers.downloadFile}
            onView={fileHandlers.handleViewFile}
            canPreview={fileHandlers.canPreviewFile}
            formatDate={formatDate}
            renderUserInfo={renderUserInfo}
            orderAsc={orderAsc}
            setOrderAsc={setOrderAsc}
            onShowSystemMessages={() => setShowSystemMessages(true)}
            systemMessagesCount={systemRespostas.length}
            visibleCount={visibleCount}
            totalUserResponses={userSortedRespostas.length}
            onLoadMore={() =>
              setVisibleCount((v) =>
                Math.min(v + 10, userSortedRespostas.length)
              )
            }
            onShowAll={() => setVisibleCount(userSortedRespostas.length)}
            onResetVisibleCount={() => setVisibleCount(10)}
            newResponse={newResponse}
            anexos={anexos}
            isSubmitting={ticketResponses.isSubmitting}
            configuracoes={configuracoes}
            onResponseChange={setNewResponse}
            onFileSelect={handleFileSelect}
            onRemoveAnexo={removeAnexo}
            onSubmit={() => {
              // Validar se pode enviar resposta
              // Só mostrar erro se for operador, não for o criador do ticket, e o ticket estiver aberto
              if (!podeEnviarResposta && usuario && (usuario.nivel === 'admin' || usuario.nivel === 'dev')) {
                if (ticket?.status_id === 1 && ticket?.usuario_id !== usuario.id) {
                  toast.error('Você precisa iniciar o atendimento antes de enviar uma resposta.');
                  return;
                }
              }
              
              if (newResponse.trim() || anexos.length > 0) {
                // Obter HTML diretamente do Quill para garantir que temos o HTML completo com spans
                let contentToSubmit = newResponse;
                if (quillRef.current) {
                  const quill = quillRef.current.getEditor();
                  const quillHTML = quill.root.innerHTML;
                  // Usar o HTML do Quill se disponível, pois pode ter os spans HTML preservados
                  if (quillHTML) {
                    contentToSubmit = quillHTML;
                  }
                }
                ticketResponses.handleSubmitResponse(contentToSubmit, anexos);
              }
            }}
            onAITextImprovement={handleAITextImprovement}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            isDragOver={isDragOver}
            ticketId={ticket?.id}
            departamentoId={ticket?.departamento_id}
            podeEnviarResposta={podeEnviarResposta}
          />
        </div>

        {/* Right Column - Sidebar com informações do ticket */}
        <div className="space-y-4 sm:space-y-5 md:space-y-6">
          <TicketInfoSidebar
            ticket={ticket}
            usuario={usuario}
            formatDate={formatDate}
            systemRespostas={systemRespostas}
            onTransferClick={() => setShowTransferirModal(true)}
            onCloseClick={() => setShowFecharModal(true)}
            onDeleteClick={() => setShowExcluirModal(true)}
            onTagsChange={(tags) => {
              if (ticket) {
                setTicket({ ...ticket, tags });
              }
            }}
            onIniciarAtendimento={handleIniciarAtendimento}
          />

          {/* Etiquetas, Tickets Relacionados e Comentários Internos - Card Unificado */}
          {ticket && (
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft space-y-6">
              {/* Etiquetas */}
              <div className="border-b border-gray-100 dark:border-gray-800/50 pb-4 last:border-b-0 last:pb-0">
              <TagsManager
                ticketId={ticket.id}
                tagsIniciais={ticket.tags || []}
                onTagsChange={(tags) => {
                  // Apenas atualizar o estado local, sem recarregar o ticket inteiro
                  setTicket({ ...ticket, tags });
                }}
                readOnly={!ticket.pode_responder}
              />
            </div>

          {/* Tickets Relacionados */}
              <div className="border-b border-gray-100 dark:border-gray-800/50 pb-4 last:border-b-0 last:pb-0">
              <RelatedTickets
                ticketId={ticket.id}
                readOnly={!ticket.pode_responder}
              />
            </div>

              {/* Comentários Internos */}
              <div>
              <InternalComments ticketId={ticket.id} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <TicketModals
        showTransferirModal={showTransferirModal}
        showFecharModal={showFecharModal}
        showExcluirModal={showExcluirModal}
        showDeleteMessageModal={ticketResponses.messageToDelete !== null}
        messageToDelete={ticketResponses.messageToDelete}
        isTransferindo={ticketActions.isTransferindo}
        isFechando={ticketActions.isFechando}
        isExcluindo={ticketActions.isExcluindo}
        isDeletingMessage={ticketResponses.isDeletingMessage}
        departamentos={departamentos}
        departamentoSelecionado={departamentoSelecionado}
        ticket={ticket}
        onTransferir={() => {
          if (departamentoSelecionado) {
            ticketActions.handleTransferirTicket(departamentoSelecionado);
          }
        }}
        onFechar={ticketActions.handleFecharTicket}
        onExcluir={ticketActions.handleDelete}
        onDeleteMessage={ticketResponses.confirmDeleteMessage}
        onCloseTransferir={() => setShowTransferirModal(false)}
        onCloseFechar={() => setShowFecharModal(false)}
        onCloseExcluir={() => setShowExcluirModal(false)}
        onCloseDeleteMessage={() => {
          ticketResponses.setMessageToDelete(null);
        }}
        onDepartamentoChange={setDepartamentoSelecionado}
      />

      {/* Modais de Visualização de Arquivos */}
      <FilePreviewModals
        showImagePreview={fileHandlers.showImagePreview}
        selectedImage={fileHandlers.selectedImage}
        showPdfPreview={fileHandlers.showPdfPreview}
        pdfPreview={fileHandlers.pdfPreview}
        pdfNumPages={fileHandlers.pdfNumPages}
        pdfPageNumber={fileHandlers.pdfPageNumber}
        pdfRenderWidth={pdfRenderWidth}
        onCloseImage={fileHandlers.closeImagePreview}
        onClosePdf={fileHandlers.closePdfPreview}
        onPdfPageChange={(page) => fileHandlers.setPdfPageNumber(page)}
        onPdfLoadSuccess={(numPages) => fileHandlers.setPdfNumPages(numPages)}
      />

      {/* Modal de Timeline do Ticket */}
      <SystemMessagesModal
        show={showSystemMessages}
        systemRespostas={systemRespostas}
        ticket={ticket}
        formatDate={formatDate}
        onClose={() => setShowSystemMessages(false)}
      />

      {/* Estilos movidos para styles/ticketDetail.css */}
    </div>
  );
}
